import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { MemberTaskFeedItem, MemberTaskFeedItemTypes, Task, TaskGroupSummary } from 'modules/Tasks/types';

dayjs.extend(utc);

function dueDay(item: MemberTaskFeedItem): string | null {
    const due = item.item_type === MemberTaskFeedItemTypes.GROUP ? item.group.next_due_date : item.task.due_date;
    return due ? dayjs.utc(due).format('YYYY-MM-DD') : null;
}

function createdAt(item: MemberTaskFeedItem): string {
    return item.item_type === MemberTaskFeedItemTypes.GROUP ? item.group.created_at : item.task.created_at;
}

function priorityOf(item: MemberTaskFeedItem): number {
    return item.item_type === MemberTaskFeedItemTypes.GROUP ? item.group.priority : item.task.priority;
}

/**
 * Compare by due date ascending at day granularity (nulls last), then higher priority first, then
 * oldest created_at. Day keys and ISO 8601 created_at both sort correctly with a lexicographic compare.
 */
function compareMemberTaskFeedItems(a: MemberTaskFeedItem, b: MemberTaskFeedItem): number {
    const aDue = dueDay(a);
    const bDue = dueDay(b);
    if (aDue !== bDue) {
        if (aDue === null) {
            return 1;
        }
        if (bDue === null) {
            return -1;
        }
        return aDue.localeCompare(bDue);
    }
    const priorityDiff = priorityOf(b) - priorityOf(a);
    if (priorityDiff !== 0) {
        return priorityDiff;
    }
    return createdAt(a).localeCompare(createdAt(b));
}

/**
 * Merge group summaries and ungrouped tasks into one feed list, ordered for the Tasks page:
 * earliest due date first (undated last), then higher priority, then oldest created_at.
 */
export default function buildMemberTaskFeedItems(
    groups: TaskGroupSummary[],
    ungroupedTasks: Task[]
): MemberTaskFeedItem[] {
    const groupItems: MemberTaskFeedItem[] = groups.map((group) => ({
        item_type: MemberTaskFeedItemTypes.GROUP,
        group,
    }));
    const taskItems: MemberTaskFeedItem[] = ungroupedTasks.map((task) => ({
        item_type: MemberTaskFeedItemTypes.TASK,
        task,
    }));

    return [...groupItems, ...taskItems].sort(compareMemberTaskFeedItems);
}
